import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import CustomTable from "../../../../../../../common/customTable/CustomTable";
import { UkEtsPortStaysColumns, UKETSEtsPortstaysColumnsData, } from "../../../../../../../constants/CustomTableHeaders";
import axiosHttp from "../../../../../../../services/interceptor";
import Loader from "../../../../../../../common/Loader/Loader";
import "./UKETSPortstays.scss";

const UKETSPortStays = () => {
  const [ukEtsPortStayData, setUkEtsPortStayData] = useState([]);
  const [loading, setLoading] = useState(false);

  const reportDetails = useSelector(
    (state) => state?.reports?.selectedReportDetails
  );

  const sanitizeKey = (value) => {
    if (!value) return "";

    return value
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const fetchUkEtsPortStaysData = async () => {
    setLoading(true);

    try {
      const response = await axiosHttp({
        url: `api/UkEts/GetUkEtsInPortActivities?childReportId=${reportDetails.ReportMstId}`,
        method: "GET",
      });

      if (response?.data?.data) {
        setUkEtsPortStayData(response.data.data);
      } else if (response?.data) {
        setUkEtsPortStayData(response.data);
      } else {
        setUkEtsPortStayData([]);
      }
    } catch (error) {
      console.error(error);
      setUkEtsPortStayData([]);
    } finally {
      setLoading(false);
    }
  };


  const fuelNames = useMemo(() => {
    const fuelSet = new Set();

    ukEtsPortStayData?.ukEtsInPortActivitiesList?.forEach((port) => {
      port?.portActivityConsumptions?.forEach((item) => {
        if (item?.fuelName) {
          fuelSet.add(item.fuelName);
        }
      });

      port?.portActivityConsumerWiseConsumptions?.forEach((item) => {
        if (item?.fuelName) {
          fuelSet.add(item.fuelName);
        }
      });

      port?.portActivityMiNcConsumptions?.forEach((item) => {
        if (item?.fuelName) {
          fuelSet.add(item.fuelName);
        }
      });

      port?.portActivityConsumerWiseMiNcConsumptions?.forEach((item) => {
        if (item?.fuelName) {
          fuelSet.add(item.fuelName);
        }
      });
    });

    return Array.from(fuelSet);
  }, [ukEtsPortStayData]);


  const getFuelConsumption = (port, fuelName) => {
    const fuel = port?.portActivityConsumptions?.find(
      (item) =>
        item?.fuelName?.toLowerCase() === fuelName?.toLowerCase()
    );

    return fuel?.consumption ?? 0;
  };


  const getConsumerWiseFuelConsumption = (
    port,
    fuelName,
    consumerCode
  ) => {
    const fuel =
      port?.portActivityConsumerWiseConsumptions?.find(
        (item) =>
          item?.fuelName?.toLowerCase() === fuelName?.toLowerCase() &&
          item?.consumerCode?.toLowerCase() ===
          consumerCode?.toLowerCase()
      );

    return fuel?.consumption ?? 0;
  };


  const getMncConsumption = (port, fuelName) => {
    const fuel = port?.portActivityMiNcConsumptions?.find(
      (item) =>
        item?.fuelName?.toLowerCase() === fuelName?.toLowerCase()
    );

    return fuel?.consumption ?? 0;
  };


  const getConsumerWiseMncConsumption = (
    port,
    fuelName,
    consumerCode
  ) => {
    const fuel =
      port?.portActivityConsumerWiseMiNcConsumptions?.find(
        (item) =>
          item?.fuelName?.toLowerCase() === fuelName?.toLowerCase() &&
          item?.consumerCode?.toLowerCase() ===
          consumerCode?.toLowerCase()
      );

    return fuel?.consumption ?? 0;
  };


  const roundTo5 = (value) => {
    return Number(value ?? 0).toFixed(5);
  };


  const consumerCodes = useMemo(() => {
    const consumers = new Set();

    ukEtsPortStayData?.ukEtsInPortActivitiesList?.forEach((port) => {
      port?.portActivityConsumerWiseConsumptions?.forEach((item) => {
        if (item?.consumerCode?.trim()) {
          consumers.add(item.consumerCode.trim().toUpperCase());
        }
      });

      port?.portActivityConsumerWiseMiNcConsumptions?.forEach((item) => {
        if (item?.consumerCode?.trim()) {
          consumers.add(item.consumerCode.trim().toUpperCase());
        }
      });
    });

    const priorityConsumers = ["ME", "AE", "BOILER"];

    return Array.from(consumers).sort((a, b) => {
      const indexA = priorityConsumers.indexOf(a);
      const indexB = priorityConsumers.indexOf(b);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (indexA !== -1) {
        return -1;
      }

      if (indexB !== -1) {
        return 1;
      }

      return a.localeCompare(b);
    });
  }, [ukEtsPortStayData]);


  const tableData = useMemo(() => {
    return (
      ukEtsPortStayData?.ukEtsInPortActivitiesList?.map(
        (port, index) => {
          const row = {
            portNumber: index + 1,
            portName: port?.portName || "",
            arrivalTime: port?.arrivalTime || "",
            departureTime: port?.departureTime || "",

            timeInPort: port?.timeInPort ?? 0,
            timeInPortAtBerth: port?.timeInPortAtBerth ?? 0,
            timeInPortInMovement:
              port?.timeInPortInMovement ?? 0,

            co2Ets: port?.co2Ets ?? 0,
            ch4Ets: port?.ch4Ets ?? 0,
            n2oEts: port?.n2oEts ?? 0,
            meEts: port?.meEts ?? 0,
          };


          fuelNames.forEach((fuelName) => {
            const fuelKey = sanitizeKey(fuelName);


            row[`fuel_${fuelKey}`] = roundTo5(
              getFuelConsumption(port, fuelName)
            );


            consumerCodes.forEach((consumerCode) => {
              const consumerKey = sanitizeKey(consumerCode);

              row[
                `consumer_${consumerKey}_fuel_${fuelKey}`
              ] = roundTo5(
                getConsumerWiseFuelConsumption(
                  port,
                  fuelName,
                  consumerCode
                )
              );
            });


            row[`mnc_${fuelKey}`] = roundTo5(
              getMncConsumption(port, fuelName)
            );


            consumerCodes.forEach((consumerCode) => {
              const consumerKey = sanitizeKey(consumerCode);

              row[
                `consumer_${consumerKey}_mnc_${fuelKey}`
              ] = roundTo5(
                getConsumerWiseMncConsumption(
                  port,
                  fuelName,
                  consumerCode
                )
              );
            });
          });

          return row;
        }
      ) || []
    );
  }, [
    ukEtsPortStayData,
    fuelNames,
    consumerCodes,
  ]);


  const neverHideColumns = new Set([
    "portNumber",
    "portName",
    "arrivalTime",
    "departureTime",
  ]);

  const zeroColumnIds = useMemo(() => {
    if (!tableData?.length) {
      return new Set();
    }

    const allKeys = new Set();

    tableData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        allKeys.add(key);
      });
    });

    const allZero = new Set();

    allKeys.forEach((key) => {
      if (neverHideColumns.has(key)) {
        return;
      }

      const isAllZero = tableData.every((row) => {
        const value = parseFloat(row[key]);

        return !isNaN(value) && value === 0;
      });

      if (isAllZero) {
        allZero.add(key);
      }
    });

    return allZero;
  }, [tableData]);


  const compactHeadCell = (
    existingSx = {},
    valign = "middle"
  ) => ({
    sx: {
      padding: "6px 6px",

      "& .Mui-TableHeadCell-Content": {
        height: "100%",
        alignItems:
          valign === "top" ? "flex-start" : "center",
        display: "flex",
      },

      ...existingSx,
    },
  });


  const columns = useMemo(() => {

    const fuelColumns = fuelNames.map((fuelName) => {
      const fuelKey = sanitizeKey(fuelName);

      return {
        id: `fuel_${fuelKey}`,

        header: (
          <div
            style={{
              textAlign: "center",
              width: "100%",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            <div>{fuelName}</div>
            <div>CONS (MT)</div>
          </div>
        ),

        accessorKey: `fuel_${fuelKey}`,

        muiTableHeadCellProps: compactHeadCell(
          {},
          "middle"
        ),
      };
    });

    const consumerFuelColumns = consumerCodes.flatMap(
      (consumerCode) => {
        const consumerKey = sanitizeKey(consumerCode);

        return fuelNames.map((fuelName) => {
          const fuelKey = sanitizeKey(fuelName);

          return {
            id: `consumer_${consumerKey}_fuel_${fuelKey}`,

            header: (
              <div
                style={{
                  textAlign: "center",
                  width: "100%",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                }}
              >
                <div>{consumerCode}</div>
                <div>{fuelName}</div>
                <div>CONS (MT)</div>
              </div>
            ),

            accessorKey: `consumer_${consumerKey}_fuel_${fuelKey}`,

            muiTableHeadCellProps: compactHeadCell(
              {},
              "middle"
            ),
          };
        });
      }
    );


    const hasMncData =
      ukEtsPortStayData?.ukEtsInPortActivitiesList?.some(
        (port) =>
          port?.portActivityMiNcConsumptions?.length > 0
      );

    const mncColumns = hasMncData
      ? fuelNames
        .filter((fuelName) =>
          ukEtsPortStayData?.ukEtsInPortActivitiesList?.some(
            (port) =>
              port?.portActivityMiNcConsumptions?.some(
                (item) =>
                  item?.fuelName?.toLowerCase() ===
                  fuelName?.toLowerCase()
              )
          )
        )
        .map((fuelName) => {
          const fuelKey = sanitizeKey(fuelName);

          return {
            id: `mnc_${fuelKey}`,

            header: (
              <div
                style={{
                  textAlign: "center",
                  width: "100%",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                <div>Mi,NC</div>
                <div>{fuelName}</div>
              </div>
            ),

            accessorKey: `mnc_${fuelKey}`,

            muiTableHeadCellProps: compactHeadCell(
              {},
              "middle"
            ),
          };
        })
      : [];


    const consumerMncColumns = consumerCodes.flatMap(
      (consumerCode) => {
        const consumerKey = sanitizeKey(consumerCode);

        return fuelNames
          .filter((fuelName) =>
            ukEtsPortStayData?.ukEtsInPortActivitiesList?.some(
              (port) =>
                port?.portActivityConsumerWiseMiNcConsumptions?.some(
                  (item) =>
                    item?.fuelName?.toLowerCase() ===
                    fuelName?.toLowerCase() &&
                    item?.consumerCode?.toUpperCase() ===
                    consumerCode.toUpperCase()
                )
            )
          )
          .map((fuelName) => {
            const fuelKey = sanitizeKey(fuelName);

            return {
              id: `consumer_${consumerKey}_mnc_${fuelKey}`,

              header: (
                <div
                  style={{
                    textAlign: "center",
                    width: "100%",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                  }}
                >
                  <div>{consumerCode}</div>
                  <div>Mi,NC</div>
                  <div>{fuelName}</div>
                </div>
              ),

              accessorKey: `consumer_${consumerKey}_mnc_${fuelKey}`,

              muiTableHeadCellProps: compactHeadCell(
                {},
                "middle"
              ),
            };
          });
      }
    );


    const sizeMap = {
      portNumber: 100,
      isSampleVoyage: 100,
      portOfDeparture: 170,
      portOfArrival: 170,
      departureTime: 250,
      arrivalTime: 250,
    };


    const twoLineHeader = (line1, line2) => (
      <div
        style={{
          textAlign: "center",
          width: "100%",
          lineHeight: "1.6",
        }}
      >
        <div>{line1}</div>
        <div>{line2}</div>
      </div>
    );

    const patchedFixedColumns = (
      UkEtsPortStaysColumns || []
    )
      .filter(Boolean)
      .map((col) => {
        if (!col) {
          return col;
        }

        const patched = {
          ...col,

          size:
            sizeMap[col.id] ??
            col.size ??
            170,

          muiTableHeadCellProps: compactHeadCell(
            col.muiTableHeadCellProps?.sx,
            "middle"
          ),
        };

        if (col.id === "portNumber") {
          patched.header = twoLineHeader(
            "Port",
            "Number"
          );
        }

        return patched;
      });


    //ETS COLUMNS

    const patchedEtsColumns = (
      UKETSEtsPortstaysColumnsData || []
    )
      .filter(Boolean)
      .map((col) => ({
        ...col,

        size: 100,

        muiTableHeadCellProps: compactHeadCell(
          col.muiTableHeadCellProps?.sx,
          "middle"
        ),
      }));

    const allColumns = [
      ...patchedFixedColumns,

      ...fuelColumns,

      ...consumerFuelColumns,

      ...mncColumns,

      ...consumerMncColumns,

      ...patchedEtsColumns,
    ];

    /*
     * Remove columns where every row contains 0.
     */
    return allColumns.filter(
      (column) => !zeroColumnIds.has(column.id)
    );
  }, [
    fuelNames,
    consumerCodes,
    ukEtsPortStayData,
    zeroColumnIds,
  ]);

  useEffect(() => {
    fetchUkEtsPortStaysData();
  }, []);

  const columnOrder = useMemo(() => {
    return columns.map((column) => column.id);
  }, [columns]);

  return (
    <>
      {loading ? (
        <div className="loader">
          <Loader />
        </div>
      ) : (
        <>
          {ukEtsPortStayData && (
            <div className="UKETS-custom-table-container">
              <CustomTable
                columnsData={columns}
                renderTableData={tableData}
                isUkEtsChildReport={true}
                customProps={{
                  isLoading: loading,
                  columnOrder: columnOrder,
                  enableSort: false,
                  enableToptoolbar: false,
                  enableColumnPinning: true,
                  enableColumnVirtualization: false,
                  enableRowVirtualization: true,
                  columnPinning: {
                    left: [
                      "portNumber",
                      "portName",
                      "arrivalTime",
                      "departureTime",
                    ],
                  },

                  muiTableHeadCellProps: {
                    sx: {
                      verticalAlign: "middle",

                      "& .Mui-TableHeadCell-Content": {
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                      },

                      "& .Mui-TableHeadCell-Content-Wrapper": {
                        textAlign: "center",
                        width: "100%",
                      },
                    },
                  },
                }}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default UKETSPortStays;